import React, { useEffect, useState, useRef, useContext } from 'react';
import { Row, Col, Card, Input, Container, FormGroup, Label, Table, Modal, ModalBody, ModalFooter } from 'reactstrap';
import styles from './DeviceDetails.module.css';
import moment from 'moment';
import { DropDownInput } from '../../components/inputs/DropDownInput';
import { Spinner } from 'react-bootstrap';
import SpinnerButton from '../../components/shared/SpinnerButton/SpinnerButton';
import SimpleReactValidator from 'simple-react-validator';
import DateField from '../shared/DateField/DateField';
import { ReactComponent as Chevron } from '../../assets/images/arrow-cheveron-down.svg';
import { useHistory } from 'react-router';
import InfoTooltip from '../shared/InfoTooltip/InfoTooltip';
import useLoadAtTop from '../_ReactHooks/useLoadAtTop';
import { ReactComponent as AddIcon } from '../../assets/images/plus.svg';
import CheckboxInput from '../shared/CheckboxInput/CheckboxInput';
import getClientContent from '../_BrandingProvider/brandContent';
import { BrandingContext } from '../contexts/ContractContext';
import { SalesContext } from '../contexts/SalesContext';
import { useCookies } from 'react-cookie';

const DeviceDetails = () => {
    useLoadAtTop();
    const history = useHistory();
    const [cookies] = useCookies();
    const context = useContext(SalesContext);
    const branding = useContext(BrandingContext);
    const clientId = branding.clientId || 'AIG';
    const clientLower = clientId?.toLowerCase();
    const { variableText } = getClientContent(clientId);
    const claimPhoneNumber = variableText?.claim?.phone;
    const [deviceOptions, setDeviceOptions] = useState([]);
    const [deviceData, setDeviceData] = useState([]);
    const [amountData, setAmountData] = useState([]);
    const [planPrice, setPlanPrice] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [dateError, setDateError] = useState(false);
    const [serialNumber, setSerialNumber] = useState('');
    const [isInvalidSerial, setIsInvalidSerial] = useState(false);
    const [servicePlan, setServicePlan] = useState('');
    const [planError, setPlanError] = useState(false);
    const [coverageError, setCoverageError] = useState(false);
    const [devicesCheckbox, setDevicesCheckbox] = useState(false);
    const [validationFired, setValidationFired] = useState(false);
    const [, forceUpdate] = useState();
    const [showModal, setShowModal] = useState(false);
    const [rowIndex, setRowIndex] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [spinner, setSpinner] = useState(false);
    const [planData, setPlanData] = useState({});
    const [multiDeviceOfferId, setMultiDeviceOfferId] = useState('');
    const [isInvalidPlan, setIsInvalidPlan] = useState(false);
    const [serialErrorMsg, setSerialErrorMsg] = useState('');
    const [serialValidationFlag, setSerialValidationFlag] = useState(false);
    const validator = useRef(
        new SimpleReactValidator({
            autoForceUpdate: {
                forceUpdate: forceUpdate
            },
            messages: {
                phone: 'Please enter a valid phone number.',
                email: 'Please enter a valid email address.',
                required: ':attribute is required.'
            },
            element: (message) => (
                <div
                    className='errorMsg'
                    role='alert'
                    aria-live='polite'
                >
                    {message.charAt(0).toUpperCase() + message.slice(1)}
                </div>
            )
        })
    );
    const selectedPlan = history.location?.state?.selectedPlan || context.data?.deviceData?.selectedPlan || '';
    const isSinglePlan = selectedPlan === 'Single Device Plan (2-year)' ? true : false;
    const deviceNumber = isSinglePlan ? 1 : 3;
    const disabled = deviceNumber === deviceData?.length;
    const planInfo = isSinglePlan
        ? {
              heading: 'Add device information to get a quote.',
              subheading: 'Add 1 device to your plan.',
              description: `${deviceData?.length} of ${deviceNumber} device added`,
              actionType: 'Change Device',
              actionInfo: 'This will remove the added device and start a new quote. Would you like to proceed?'
          }
        : {
              heading: 'Add device information to get started.',
              subheading: 'Add up to 3 devices to your plan.',
              description: `${deviceData?.length} of ${deviceNumber} devices added`,
              actionType: 'Remove',
              actionInfo: `This will remove the added ${selectedRow?.device} device. Would you like to proceed?`
          };
    const serviceOptions = [
        { data: 'applecare', display: 'AppleCare+' },
        { data: 'theftandloss', display: 'AppleCare+ Theft and Loss' },
        { data: 'at&t', display: 'AT&T' },
        { data: 'tmobile', display: 'T-Mobile' },
        { data: 'verizon', display: 'Verizon' },
        { data: 'other', display: 'Other' }
    ];

    useEffect(() => {
        getDeviceOptions();
        getPricingData();
        getSerialValidationFlag();
        const currentData = context.data?.deviceData;

        if (currentData) {
            setDeviceData(currentData?.deviceInfo);
            setPlanPrice(currentData?.planPrice);
            setPlanData(currentData?.activePlan);
        }
    }, [clientId]);

    useEffect(() => {
        context.updateDeviceData({
            ...context.data?.deviceData,
            deviceInfo: deviceData,
            planPrice: planPrice,
            activePlan: planData,
            selectedPlan: selectedPlan
        });
    }, [deviceData, planPrice, planData]);

    const getDeviceOptions = () => {
        fetch(`api/asset/types/${clientLower}`)
            .then((res) => res.json())
            .then((data) => {
                const options = data?.map((item) => {
                    return {
                        data: item.assetTypeId,
                        display: item.assetTypeName
                    };
                });
                setDeviceOptions(options);
            });
    };

    const getPricingData = () => {
        fetch(`api/pricing/unbundle/riskprofiles/us/${clientLower}`)
            .then((res) => res.json())
            .then((data) => {
                const planInfo = data?.find((opt) => opt.riskProfileName === selectedPlan);
                const pricingInfo = planInfo?.pricingInfo || [];
                setAmountData(pricingInfo);
                setPlanData(planInfo);

                if (selectedPlan === 'Multi-device Plan (2-year)') {
                    const amount = pricingInfo[0]?.amount || null;
                    const offerId = pricingInfo[0]?.offerId || null;
                    setPlanPrice(amount);
                    setMultiDeviceOfferId(offerId);
                }
            });
    };

    const getSerialValidationFlag = () => {
        fetch(`api/helper/serialNumber/validation/flag/${clientLower}`)
            .then((res) => res.json())
            .then((data) => {
                setSerialValidationFlag(data?.enabled);
            });
    };

    const validateSerialNumber = async () => {
        const response = await fetch(`api/contract/validate/serialNumber/${serialNumber}/${clientLower}`, {
            method: 'Get',
            headers: {
                'Content-Type': 'application/json',
                XSRFTOKEN: cookies.XSRFTOKEN
            }
        });
        const data = await response.json();
        return data;
    };

    const handleDeviceSelection = (opt) => {
        setSelectedDevice(opt);
    };

    const handlePurchaseDate = (val) => {
        const isValidDate = moment(val, 'MM/DD/YYYY')?.isValid();

        if (val === '') {
            setPurchaseDate('');
            setDateError(false);
            return;
        } else if (!isValidDate) {
            setDateError(false);
            return;
        } else {
            setPurchaseDate(val);
        }
        const today = moment();
        const selectedDate = moment(val);
        const days = today.diff(selectedDate, 'days');
        if (isValidDate && isSinglePlan && days > 60) {
            setDateError(true);
        } else {
            setDateError(false);
        }
    };

    const handleSerialNumberChange = (e) => {
        const { value } = e.target;

        if (!value) {
            setIsInvalidSerial(false);
            setSerialErrorMsg('');
        }
        setSerialNumber(value);
    };

    const handleServicePlan = (opt) => {
        setServicePlan(opt);

        if (opt === 'theftandloss') {
            setCoverageError(true);
        } else {
            setCoverageError(false);
        }
    };

    const handleContinue = () => {
        if (!isSinglePlan && deviceData?.length < deviceNumber && !devicesCheckbox) {
            setPlanError(true);
        } else {
            setPlanError(false);
            history.push('/buy/reviewplan');
        }
    };

    const handleChangePlan = () => {
        history.push('/');
    };

    const handleCheckbox = () => {
        setDevicesCheckbox(!devicesCheckbox);
    };

    const updateItem = (item, index) => {
        setRowIndex(index);
        setSelectedRow(item);
        setShowModal(!showModal);
    };

    const updateDeviceInfo = () => {
        const updatedData = deviceData.filter((data, i) => i !== rowIndex);
        setDeviceData(updatedData);
        setShowModal(!showModal);
        if (isSinglePlan) {
            setPlanPrice(null);
        }
    };

    const SerialNumberInfo = () => {
        return (
            <div className={styles.description}>
                <p className='mb-2'>Learn how to find your device's serial number:</p>
                <ul className='mb-0'>
                    <li>
                        <a
                            href='https://support.apple.com/en-us/108037'
                            target='_blank'
                            className='font-weight-bold'
                        >
                            iPhone and iPad
                        </a>
                    </li>
                    <li>
                        <a
                            href='https://support.apple.com/en-us/108040'
                            target='_blank'
                            className='font-weight-bold'
                        >
                            Apple Watch
                        </a>
                    </li>
                </ul>
            </div>
        );
    };

    const handleAddClick = async (e) => {
        e.preventDefault();
        setValidationFired(true);
        setIsInvalidPlan(false);
        setSerialErrorMsg('');

        if (validator.current.allValid() && !coverageError && !dateError) {
            if (deviceData?.length === 0) {
                setDevicesCheckbox(false);
                setPlanError(false);
            }

            const isSerialDuplicate = deviceData?.some(
                (data) => data.serial?.toLowerCase() === serialNumber?.toLowerCase()
            );
            if (isSerialDuplicate) {
                setIsInvalidSerial(false);
                setSerialErrorMsg('This serial has already been added below.');
                return;
            }

            if (serialValidationFlag) {
                setSpinner(true);
                const result = await validateSerialNumber();

                if (result?.validSerialNumber && !result?.duplicateOnAnotherContract) {
                    fetch(`vendor/apple/acc/device/configuration/${serialNumber}/${clientLower}`, {
                        method: 'Get',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }).then((res) => {
                        if (res.ok) {
                            res.json().then((data) => {
                                setSpinner(false);

                                let newData;
                                const deviceSelected = deviceOptions?.find((opt) => opt.data === selectedDevice) || '';
                                const selectedService = serviceOptions?.find((opt) => opt.data === servicePlan) || '';
                                const date = moment(purchaseDate).format('YYYY-MM-DD');
                                const details = {
                                    device: deviceSelected?.display,
                                    model: data?.deviceConfigResponses?.deviceConfiguration || '',
                                    serial: data?.serialNumber || serialNumber,
                                    plan: selectedService?.display || null,
                                    assetName: data?.assetModel?.assetName || '',
                                    assetId: data?.assetModel?.assetId || '',
                                    basicMaterial: data?.deviceConfigResponses?.basicMaterial || '',
                                    purchaseDate: date || purchaseDate || '',
                                    offerId: multiDeviceOfferId || ''
                                };

                                if (data.successful === true) {
                                    setValidationFired(false);
                                    setIsInvalidSerial(false);

                                    if (isSinglePlan) {
                                        const assetDetails = amountData?.find(
                                            (opt) => opt.assetId === data?.assetModel?.assetId
                                        );
                                        const amount = assetDetails?.amount || null;
                                        details.offerId = assetDetails?.offerId || '';
                                        setPlanPrice(amount);

                                        if (!assetDetails?.offerId || !amount) {
                                            setIsInvalidPlan(true);
                                            return;
                                        }
                                    }
                                    if (deviceData?.length < 3) {
                                        newData = [...deviceData, details];
                                        setDeviceData(newData);
                                        resetFields();
                                    }
                                } else {
                                    setIsInvalidSerial(true);
                                }
                            });
                        }
                    });
                } else {
                    setSpinner(false);
                    setIsInvalidSerial(false);
                    setSerialErrorMsg(
                        'This serial is already covered under another plan and cannot be added to this plan.'
                    );
                }
            }
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };

    const resetFields = () => {
        setSelectedDevice('');
        setPurchaseDate('');
        setSerialNumber('');
        setServicePlan('');
        validator.current.hideMessages();
    };

    return (
        <div className={styles.contentWrapper}>
            <Container className={styles.contentContainer}>
                <div className={styles.headerContainer}>
                    <h1>{selectedPlan}</h1>
                    <p>{planInfo?.heading}</p>
                </div>
                <div className={styles.planContainer}>
                    <a
                        className={styles.planLink}
                        onClick={handleChangePlan}
                    >
                        <Chevron className={styles.arrowIcon} /> Change Plan
                    </a>
                    <div
                        className={`${styles.planPrice} ${isSinglePlan && deviceData?.length === 0 && styles.disabled}`}
                    >
                        Plan Price <b className='ml-2'>${planPrice?.toFixed(2)}</b>
                    </div>
                </div>
                <Card className={styles.deviceInfoCard}>
                    <h2>{planInfo?.subheading}</h2>
                    <Row className='multiColWrapper mt-3'>
                        <Col
                            sm='12'
                            md='6'
                            lg={isSinglePlan ? '4' : '3'}
                        >
                            <FormGroup className={validationFired && !selectedDevice && 'form-error'}>
                                <Label
                                    for='device'
                                    className='form-label'
                                >
                                    Device
                                </Label>
                                <span
                                    aria-hidden='true'
                                    className='required-indicator'
                                >
                                    *
                                </span>
                                <DropDownInput
                                    name='device'
                                    options={deviceOptions}
                                    className='form-control'
                                    disabled={disabled}
                                    onChangedValue={handleDeviceSelection}
                                    placeholder='Please Select...'
                                    defaultValue={selectedDevice}
                                    value={selectedDevice}
                                />
                                {validator.current.message('device', selectedDevice, 'required')}
                            </FormGroup>
                        </Col>
                        <Col
                            sm='12'
                            md='6'
                            lg={isSinglePlan ? '4' : '3'}
                        >
                            <FormGroup className={((validationFired && !purchaseDate) || dateError) && 'form-error'}>
                                <Label
                                    for='purchaseDate'
                                    className='form-label'
                                >
                                    Date of Purchase
                                </Label>
                                <span
                                    aria-hidden='true'
                                    className='required-indicator'
                                >
                                    *
                                </span>
                                <DateField
                                    name='purchaseDate'
                                    showError={false}
                                    disabled={disabled}
                                    onDateChange={handlePurchaseDate}
                                    resetDate={purchaseDate === ''}
                                    required={true}
                                    minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 3))}
                                />
                                {validator.current.message('purchaseDate', purchaseDate, 'required', {
                                    messages: {
                                        required: 'Date of purchase is required.'
                                    }
                                })}
                                {dateError && (
                                    <div class='errorMsg'>
                                        Date of purchase cannot exceed 60 days to be eligible for coverage. You may be
                                        required to provide proof of purchase when filing a claim.
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                        <Col
                            sm='12'
                            md='6'
                            lg={isSinglePlan ? '4' : '3'}
                        >
                            <FormGroup
                                className={
                                    ((validationFired && !serialNumber) || isInvalidSerial || serialErrorMsg)
                                    && 'form-error'
                                }
                            >
                                <Label
                                    htmlFor='serialNumber'
                                    className='form-label'
                                >
                                    Serial Number
                                </Label>
                                <span
                                    aria-hidden='true'
                                    className='required-indicator'
                                >
                                    *
                                </span>
                                <span className={styles.infoIcon}>
                                    <InfoTooltip
                                        customClass={styles.infoText}
                                        message={<SerialNumberInfo />}
                                    />
                                </span>
                                <Input
                                    className='form-control'
                                    id='serialNumber'
                                    name='serialNumber'
                                    value={serialNumber}
                                    disabled={disabled}
                                    onChange={handleSerialNumberChange}
                                    aria-required='true'
                                    maxLength={100}
                                />
                                {validator.current.message('serialNumber', serialNumber, 'required')}
                                {isInvalidSerial && (
                                    <div className='errorMsg'>
                                        We are unable to validate the serial number entered. Please try again.
                                    </div>
                                )}
                                {serialErrorMsg && <div className='errorMsg'>{serialErrorMsg}</div>}
                            </FormGroup>
                        </Col>
                        {!isSinglePlan && (
                            <Col
                                sm='12'
                                md='6'
                                lg='3'
                            >
                                <FormGroup
                                    className={((validationFired && !servicePlan) || coverageError) && 'form-error'}
                                >
                                    <Label
                                        for='service'
                                        className='form-label'
                                    >
                                        Primary Coverage Provider Plan
                                    </Label>
                                    <span
                                        aria-hidden='true'
                                        className='required-indicator'
                                    >
                                        *
                                    </span>
                                    <DropDownInput
                                        name='service'
                                        options={serviceOptions}
                                        className='form-control'
                                        disabled={disabled}
                                        onChangedValue={handleServicePlan}
                                        placeholder='Please Select...'
                                        defaultValue={servicePlan}
                                        value={servicePlan}
                                    />
                                    {validator.current.message('service', servicePlan, 'required', {
                                        messages: {
                                            required: 'Primary coverage provider plan is required.'
                                        }
                                    })}
                                    {coverageError && (
                                        <div className='errorMsg'>
                                            Your current plan includes coverage for theft and loss. Please verify your
                                            coverage with Apple.
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                        )}
                    </Row>
                    <div className={styles.btnContainer}>
                        <button
                            className={`btn btn-secondary ${styles.relative}`}
                            onClick={handleAddClick}
                            disabled={disabled || spinner}
                        >
                            {spinner && (
                                <span className={styles.loader}>
                                    <Spinner
                                        color='light'
                                        animation={spinner && 'border'}
                                    />
                                </span>
                            )}
                            <span className={spinner && styles.hideText}>
                                <AddIcon className={styles.addIcon} />
                                <span>Add Device</span>
                            </span>
                        </button>
                    </div>
                    {deviceData?.length > 0 && (
                        <>
                            <p className='mt-4'>{planInfo?.description}</p>
                            <div className={styles.tableWrapper}>
                                <Table className={styles.deviceTable}>
                                    <thead>
                                        <tr>
                                            <th>Device</th>
                                            <th>Model</th>
                                            <th>Serial Number</th>
                                            {!isSinglePlan && <th>Primary Coverage Provider Plan</th>}
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deviceData?.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.device}</td>
                                                <td>{item.model}</td>
                                                <td>{item.serial}</td>
                                                {!isSinglePlan && <td>{item.plan}</td>}
                                                <td>
                                                    <button
                                                        className='btnLink'
                                                        onClick={() => updateItem(item, index)}
                                                    >
                                                        {planInfo.actionType}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            {!isSinglePlan && (
                                <Col className='p-0'>
                                    <CheckboxInput
                                        id='devices'
                                        name='devices'
                                        customClass='mb-0'
                                        checked={devicesCheckbox}
                                        handleCheck={handleCheckbox}
                                    >
                                        Opt out of additional devices. Manage the devices on your plan later by calling{' '}
                                        {claimPhoneNumber}.
                                    </CheckboxInput>
                                </Col>
                            )}
                        </>
                    )}
                </Card>
                {deviceData?.length > 0 && planError && !devicesCheckbox && deviceData?.length < deviceNumber && (
                    <div className='errorMsg text-center mt-4'>
                        Please add another device or opt out of coverage: {planInfo?.description}
                    </div>
                )}
                {planPrice && <div className={styles.priceInfo}>Plan Price: ${planPrice?.toFixed(2)}</div>}
                {isSinglePlan && isInvalidPlan && (
                    <div className='errorMsg text-center mt-4'>We are unable to find a plan to cover this device.</div>
                )}
                <div className={styles.btnWrapper}>
                    <SpinnerButton
                        type='primary'
                        onClick={handleContinue}
                        disabled={deviceData?.length === 0}
                        text='Buy Now'
                        ariaLabel='Click buy now to buy protection plan'
                    />
                </div>
                {showModal && (
                    <Modal
                        isOpen={showModal}
                        aria-modal='true'
                        className='md'
                        centered={true}
                    >
                        <ModalBody className={styles.updateModal}>
                            <h4 className='mb-0'>{planInfo?.actionInfo}</h4>
                        </ModalBody>
                        <ModalFooter className={styles.modalBodyContainer}>
                            <button
                                className='btn btn-secondary'
                                onClick={() => setShowModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className='btn btn-primary'
                                onClick={updateDeviceInfo}
                            >
                                {planInfo?.actionType}
                            </button>
                        </ModalFooter>
                    </Modal>
                )}
            </Container>
        </div>
    );
};

export default DeviceDetails;
